import React from 'react'
import { MessageSquareMore } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { StatusBadge } from '@/components/ui/badge'
import { GameRoomStatus, Status } from '@/redux/types/room'
import { AdvisorDistributionEntry } from '@/redux/types/usageStats'
import AdvisorDonutChart from './AdvisorDonutChart'
import {
  BG_COLORS,
  DIALOG_GRADIENTS,
  CHART_COLORS,
} from '@/utils/advisorChartUtils'

interface AdvisorChatCardProps {
  playerName: string
  playerEmail: string
  role?: string
  distribution: AdvisorDistributionEntry[]
  status: GameRoomStatus | Status
  onViewChat: () => void
}

const AdvisorChatCard: React.FC<AdvisorChatCardProps> = ({
  playerName,
  playerEmail,
  role,
  distribution,
  status,
  onViewChat,
}) => {
  const randomIdx = Math.floor(Math.random() * BG_COLORS.length)
  const bgColor = BG_COLORS[randomIdx]
  const dialogGradient = DIALOG_GRADIENTS[randomIdx]
  const displayName = playerName || playerEmail

  return (
    <div className={`flex flex-col min-h-[300px] ${bgColor} rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] border border-white/10 relative group`}>
      <div className="absolute inset-0 bg-[url('/images/create-room-bg.png')] bg-cover bg-center opacity-40 transition-opacity group-hover:opacity-60" />

      <div className='absolute z-10 top-3 left-3'>
        <StatusBadge status={status} />
      </div>

      <div className='z-[1] flex flex-col h-full p-4 pt-14'>
        <div className='text-center mb-1'>
          <p className='text-sm font-bold text-white leading-tight'>
            {displayName}
          </p>
          {role && (
            <>
              <p className='text-[11px] text-white/60 mt-0.5'>as</p>
              <p className='text-sm text-white font-semibold leading-tight'>
                {role}
              </p>
            </>
          )}
        </div>

        <div className='flex-1 flex items-center justify-center py-1'>
          <AdvisorDonutChart
            distribution={distribution}
            size={160}
            ringWidth={26}
          />
        </div>

        <div className='flex justify-between items-center pt-2'>
          <Dialog>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <button
                      className='w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors'
                      aria-label='Advisor info'
                    >
                      <span className='text-white font-bold text-base leading-none'>
                        ?
                      </span>
                    </button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Advisor Details</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <DialogContent
              className='w-[260px] p-0 overflow-hidden rounded-2xl border-0 shadow-2xl [&>button]:hidden'
              style={{ backgroundColor: dialogGradient.bg }}
            >
              <div
                className='p-4 flex flex-col items-center gap-1'
                style={{ background: dialogGradient.top }}
              >
                <p className='text-base font-bold text-white text-center leading-tight'>
                  {displayName}
                </p>
                <p className='text-[11px] text-white/60'>as</p>
                <p className='text-sm font-bold text-white text-center leading-tight mb-2'>
                  {role ?? '—'}
                </p>
                <AdvisorDonutChart
                  distribution={distribution}
                  size={190}
                  ringWidth={28}
                  showLegend={false}
                />
              </div>

              <div
                className='p-4 flex flex-col gap-3'
                style={{ backgroundColor: dialogGradient.bg }}
              >
                {distribution.length > 0 && (
                  <div className='bg-white rounded-xl p-3 max-h-[140px] overflow-y-auto flex flex-col gap-2'>
                    {distribution.map((entry, i) => (
                      <div
                        key={entry.chatbot_id}
                        className='flex items-center gap-2.5'
                      >
                        <span
                          className='inline-block w-3.5 h-3.5 rounded-full flex-shrink-0'
                          style={{
                            backgroundColor:
                              CHART_COLORS[i % CHART_COLORS.length],
                          }}
                        />
                        <span className='text-sm text-gray-800 font-medium'>
                          {entry.chatbot_name ?? entry.chatbot_id}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  className='w-full max-w-[200px] px-6 py-1.5 h-9 text-sm font-medium rounded-md transition-all mx-auto bg-white text-blue-800 hover:bg-blue-50 shadow-md hover:shadow-lg'
                  onClick={onViewChat}
                >
                  View Advisor Chat
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={onViewChat}
                  className='w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center transition-colors'
                  aria-label='Jump to chat'
                >
                  <MessageSquareMore className='w-4 h-4 text-white' />
                </button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Jump to Chat</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  )
}

export default AdvisorChatCard
